/**
 * Cloudflare Turnstile invisible-widget token helper.
 *
 * Loads the Turnstile script lazily on first call and resolves a fresh
 * single-use token via `turnstile.execute()`. Returns `null` when
 * VITE_TURNSTILE_SITE_KEY is unset (dev / unconfigured) so callers can
 * fall back to header-less requests.
 *
 * Tokens are single-use and short-lived; always fetch a fresh one before
 * each gated request; do not cache.
 */

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? "";

declare global {
  interface Window {
    turnstile?: {
      ready: (cb: () => void) => void;
      render: (container: HTMLElement | string, options: Record<string, unknown>) => string;
      execute: (widgetIdOrContainer: string) => void;
      reset: (widgetIdOrContainer?: string) => void;
      remove: (widgetIdOrContainer?: string) => void;
    };
  }
}

let scriptLoadPromise: Promise<void> | null = null;
let widgetIdPromise: Promise<string> | null = null;

function loadScript(): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("not in browser"));
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src^="${SCRIPT_URL.split("?")[0]}"]`);
    if (existing) {
      if (window.turnstile) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("turnstile script load error")), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("turnstile script load error"));
    document.head.appendChild(s);
  });
  return scriptLoadPromise;
}

function ensureWidget(): Promise<string> {
  if (widgetIdPromise) return widgetIdPromise;
  widgetIdPromise = loadScript().then(
    () =>
      new Promise<string>((resolve, reject) => {
        if (!window.turnstile) {
          reject(new Error("turnstile global missing after script load"));
          return;
        }
        window.turnstile.ready(() => {
          const container = document.createElement("div");
          container.style.position = "absolute";
          container.style.left = "-9999px";
          container.style.top = "-9999px";
          container.setAttribute("aria-hidden", "true");
          document.body.appendChild(container);
          try {
            const id = window.turnstile!.render(container, {
              sitekey: SITE_KEY,
              size: "invisible",
              callback: () => {
                /* token delivered via execute promise resolver below */
              },
            });
            resolve(id);
          } catch (err) {
            reject(err);
          }
        });
      }),
  );
  return widgetIdPromise;
}

/**
 * Fetch a fresh Turnstile token. Returns `null` if Turnstile is not
 * configured (no VITE_TURNSTILE_SITE_KEY at build time), callers
 * should send the request without the header in that case; the API
 * middleware will pass through unverified.
 */
export async function getTurnstileToken(): Promise<string | null> {
  if (!SITE_KEY) return null;
  const widgetId = await ensureWidget();
  return new Promise<string>((resolve, reject) => {
    if (!window.turnstile) {
      reject(new Error("turnstile global gone"));
      return;
    }
    // Re-render the callback for this execute call.
    const container = document.querySelector<HTMLDivElement>(`[data-cf-turnstile-id="${widgetId}"]`);
    if (container) {
      window.turnstile.reset(widgetId);
    }
    // Use a one-shot callback by re-rendering. The simplest way to get
    // a fresh token per call without re-mounting is to subscribe via
    // the global API: render a new widget per call. To avoid leaking
    // DOM nodes, we recycle through reset() on the existing widget and
    // poll the implicit response field.
    const responseField = document.querySelector<HTMLInputElement>(`input[name="cf-turnstile-response"]`);
    if (!responseField) {
      reject(new Error("turnstile response field missing"));
      return;
    }
    let elapsed = 0;
    const interval = window.setInterval(() => {
      const value = responseField.value;
      if (value) {
        window.clearInterval(interval);
        resolve(value);
        return;
      }
      elapsed += 100;
      if (elapsed > 10_000) {
        window.clearInterval(interval);
        reject(new Error("turnstile timeout"));
      }
    }, 100);
    window.turnstile.execute(widgetId);
  });
}
